import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { compose, lifecycle, withProps, branch, renderNothing, withHandlers, withState } from 'recompose'

import * as profileSelectors from 'app/modules/profile/selectors'
import * as selectors from 'app/modules/meetings/selectors'
import * as actions from 'app/modules/meetings/actions'
import { assistantAgentSettings } from 'app/modules/assistantSettings/selectors'
import { fetchAssistantAgentSettings } from 'app/modules/assistantSettings/actions'
import { defaultNewMeetingType } from 'app/modules/meetings/models'

import MeetingTypeEdit from './component'

const MEETING_ADJECTIVES = ['quick', 'informational', 'important', 'onboarding', 'platinum', 'masters']
const MEETING_NOUNS = ['demo', 'meeting', 'chat', 'call', 'talk', 'follow-up', 'check-in']

function makeMapStateToProps() {
  return (state, { params }) => {
    const slug = params.meetingTypeSlug
    const isDefault = slug === 'default'
    const isNew = slug === 'new'
    return {
      assistantAgentSettings: assistantAgentSettings(state),
      profileUrl: profileSelectors.profileUrl(state),
      profileUrlHandle: profileSelectors.profileUrlHandle(state),
      isDefault,
      meetingTypes: selectors.getMeetingTypes(state),
      isSettingsLoaded: isDefault
        ? selectors.isMeetingSettingsLoaded(state)
        : selectors.isMeetingTypesLoaded(state),
      savedSettings: isNew
        ? defaultNewMeetingType
        : isDefault
          ? selectors.getDefaultMeetingType(state)
          : selectors.getMeetingTypes(state).find(mt => mt.slug === slug),
    }
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        fetchDefaultSettings: actions.fetchMeetingSettings,
        fetchCustomSettings: actions.fetchMeetingTypes,
        updateDefaultSettings: actions.debounceUpdateMeetingSettings,
        updateCustomSettings: actions.updateMeetingType,
        fetchAssistantAgentSettings
      },
      dispatch
    ),
  }
}

export default compose(
  connect(makeMapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidMount() {
      const {
        isDefault,
        isSettingsLoaded,
        actions: { fetchDefaultSettings, fetchCustomSettings },
      } = this.props
      if (!isSettingsLoaded) {
        isDefault ? fetchDefaultSettings() : fetchCustomSettings()
      }
    },
  }),
  branch(({ isSettingsLoaded, savedSettings }) => !savedSettings || !isSettingsLoaded, renderNothing),
  withState('settings', 'setSettings', ({ savedSettings }) =>
    savedSettings.set('allowNewAppointments', true)
  ),
  withState('isTitleEmpty', 'setIsTitleEmpty', props => props.settings.name === ''),
  withState('isTitleChanged', 'setIsTitleChanged', false),
  withState('isSlugInConflict', 'setIsSlugInConflict', false),
  withState('isSlugEmpty', 'setIsSlugEmpty', props => _.get(props, 'settings.slug') === ''),
  withState('isSlugChanged', 'setIsSlugChanged', false),
  withState('isSlugReserved', 'setIsSlugReserved', false),
  withState('isSaving', 'setSaving', false),
  withState('isCopied', 'setIsCopied', false),
  withProps(props => ({
    isSlugValid: props.isDefault || (!props.isSlugInConflict && !props.isSlugEmpty && !props.isSlugReserved),
    saveSettings: props.isDefault ? props.actions.updateDefaultSettings : props.actions.updateCustomSettings,
    isSettingsChanged: !_.isEqual(props.savedSettings, props.settings),
  })),
  withProps(() => {
    const meetingNoun = _.sample(MEETING_NOUNS)
    const placeholderSlug = `${_.sample(MEETING_ADJECTIVES)}-${meetingNoun}`
    return {
      placeholderSlug,
    }
  }),
  withProps(({ assistantAgentSettings }) => ({
    confirmationEmailStatus: assistantAgentSettings.getIn(['MEETING_CONFIRMATION', 'on'], false),
    reminderEmailStatus: assistantAgentSettings.getIn(['MEETING_REMINDER', 'on'], false)
  })),
  withHandlers({
    handleCopyMeetingLink: ({ setIsCopied }) => () => {
      setIsCopied(true)
      setTimeout(() => {
        setIsCopied(false)
      }, 1000)
    },
    handleSave: ({ settings, saveSettings, setSaving, savedSettings, router, isDefault }) => () => {
      if (!isDefault && settings.slug !== savedSettings.slug) {
        saveSettings(settings.set('oldSlug', savedSettings.slug))
      } else {
        saveSettings(settings)
      }
      setSaving(true)
      setTimeout(() => {
        router.push(`/settings/calendar`)
      }, 800)
    },
    handleUpdateMeetingName: ({ settings, setSettings, setIsTitleEmpty, setIsTitleChanged }) => ({
      target: { value },
    }) => {
      setIsTitleEmpty(value === '')
      setIsTitleChanged(true)
      setSettings(settings.set('name', value))
    },
    handleDurationSelect: ({ settings, setSettings }) => ({ value }) => {
      setSettings(settings.set('duration', value))
    },
    handleChangeMeetingSlug: props => ({ target: { value }}) => {
      const {
        meetingTypes,
        setIsSlugInConflict,
        setIsSlugEmpty,
        setIsSlugChanged,
        setIsSlugReserved,
        setSettings,
        savedSettings,
        settings,
      } = props
      const newSlug = value.toLowerCase()
      if (newSlug.replace(/[a-z0-9\-]+/, '').length) return false

      const matchingMeetingTypes = meetingTypes.find(mt => mt.slug === newSlug)
      setIsSlugInConflict(!!matchingMeetingTypes && savedSettings.slug !== newSlug)
      setIsSlugEmpty(newSlug === '')
      setIsSlugChanged(true)
      setIsSlugReserved(['default', 'meeting', 'new'].includes(newSlug))
      setSettings(settings.set('slug', newSlug))
    },
    handleUpdateMeetingLocation: ({ setSettings, settings }) => ({ target: { value }}) => {
      setSettings(settings.set('location', value))
    },
    handleUpdateMeetingDescription: ({ setSettings, settings }) => value => {
      setSettings(settings.set('inviteDescription', value))
    },
    handleExitAttempt: ({ isSaving, savedSettings, settings }) => () => {
      return !isSaving && !_.isEqual(savedSettings, settings)
        ? 'You have unsaved changes. Would you like to exit without saving?'
        : true
    },
  }),
  lifecycle({
    componentDidMount() {
      const { router, route, handleExitAttempt } = this.props
      router.setRouteLeaveHook(route, handleExitAttempt)
      this.props.actions.fetchAssistantAgentSettings()
    },
  })
)(MeetingTypeEdit)



// WEBPACK FOOTER //
// ./src/scripts/components/settings/meetings/MeetingTypeEdit/index.js